<!-- Copyright 2023, Common Good Learning Tools LLC -->
<template>
	<k-dialog
		v-model="dialog_open"
		overlay-opacity="0.9"
		:max-width="dialog_max_width"
		persistent
		scrollable
		content-class="k-powerschool-import-dialog"
	><k-card>
		<k-card-title class="k-ps-dialog-title">
			<div><b class="mr-4">Export for PowerSchool SIS</b> Step {{ stage }}</div>
			<k-spacer></k-spacer>
			<k-btn
				color="secondary"
				x-small
				fab
				@click="$emit('dialog_cancel')"
			><k-icon>fas fa-xmark</k-icon></k-btn
			>
		</k-card-title>
		<k-card-text class="k-powerschool-import-card mt-3">
			<div>
				<div class="pa-2 k-ps-info-callout">
					This tool will generate a CSV file in <a
						href="https://ps.powerschool-docs.com/pssis-admin/25.1/standards-district-list"
						target="_blank">PowerSchool's standard import format</a> for standards, which you can then upload to your PowerSchool SIS.
				</div>
			</div>
			<div v-if="stage == 1">
				<div class="mt-3">
					<b>Step 1:</b> To start the process,
					<b>choose which "branches" of the framework you wish to export</b>, by checking the boxes in the
					framework tree shown at right. If you don't choose any branches and click the "GO TO STEP 2" button, the
					entire framework will be exported.
				</div>
				<div class="mt-3 mb-3 text-right">
					<i>Current Selection: <b>{{ selection_label }}</b></i>
				</div>
				<div class="mt-3 text-right">
					<k-btn
						small
						color="primary"
						@click="go_to_stage_2"
					>Go To Step 2
						<k-icon
							small
							class="ml-2"
						>fas fa-arrow-right</k-icon
						></k-btn
					>
				</div>
			</div>
			<div
				v-if="stage == 2"
				class="mt-4"
			>
				<div class="mt-3">
					<b>Step 2:</b> Configure settings per item type.
				</div>
				<table class="k-ps-type-settings-table k-sticky-table-header mt-3">
					<thead>
						<tr>
							<th>Item Type</th>
							<th>Count</th>
							<th>IsAssignmentAllowed</th>
							<th>IsActive</th>
							<th>IsCommentIncluded</th>
							<th>IsExcludedFromReports</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(val, item_type) in item_types" :key="item_type">
							<td><b>{{ item_type }}</b></td>
							<td>{{ val.count }}</td>
							<td><k-checkbox class="mt-0 pt-0" v-model="item_types[item_type].IsAssignmentAllowed" hide-details></k-checkbox></td>
							<td><k-checkbox class="mt-0 pt-0" v-model="item_types[item_type].IsActive" hide-details></k-checkbox></td>
							<td><k-checkbox class="mt-0 pt-0" v-model="item_types[item_type].IsCommentIncluded" hide-details></k-checkbox></td>
							<td><k-checkbox class="mt-0 pt-0" v-model="item_types[item_type].IsExcludedFromReports" hide-details></k-checkbox></td>
						</tr>
					</tbody>
				</table>
				<div class="d-flex justify-end mt-3">
					<k-btn
						small
						class="k-tight-btn mr-2"
						color="primary"
						@click="go_to_stage_1"
					><k-icon
						small
						class="mr-2"
					>fas fa-arrow-left</k-icon
					>Back</k-btn
					>
					<k-btn
						small
						class="k-tight-btn"
						color="primary"
						@click="go_to_stage_3"
					>Go To Step 3
						<k-icon
							small
							class="ml-2"
						>fas fa-arrow-right</k-icon
						></k-btn
					>
				</div>
			</div>
			<div
				v-if="stage == 3"
				class="mt-4"
			>
				<div class="mt-3">
					<b>Step 3:</b> Configure general export settings.
				</div>
				<div class="mt-5">
					<div v-if="needs_prefix" class="d-flex align-center my-3">
						<div class="mr-2 k-ps-settings-label">
							<k-tooltip bottom>
								<template v-slot:activator="{ on }">
									<k-icon
										v-on="on"
										small
										color="light-blue"
										class="k-ps-tooltip-icon"
									>
										fas fa-info-circle
									</k-icon>
								</template>
								<div class="k-ps-tooltip-content">
									Added to the start of the generated Identifier (e.g. MA19.1.2) to keep codes unique.
									A standard keeps its own human coding scheme code where it has one; where the same
									code is used twice, the first standard keeps it and the rest are generated. Shown
									because some standards in this framework are missing a code or share one. A prefix
									that would produce an identifier matching one of those existing codes is rejected,
									so you may need to pick a different one.
								</div>
							</k-tooltip>
							<b>Identifier Prefix:</b>
						</div>
						<k-text-field dense outlined hide-details="auto" v-bind="formFields.identifier_prefix" @input="formFields.identifier_prefix.input" @blur="formFields.identifier_prefix.blur" placeholder="e.g. MA19" class="k-ps-inline-field k-ps-settings-field"></k-text-field>
					</div>
					<div v-if="needs_prefix" class="k-ps-settings-hint">{{ identifier_strategy_hint }}</div>
					<div v-if="show_document_identifier_field" class="d-flex align-center my-3">
						<div class="mr-2 k-ps-settings-label">
							<k-tooltip bottom>
								<template v-slot:activator="{ on }">
									<k-icon
										v-on="on"
										small
										color="light-blue"
										class="k-ps-tooltip-icon"
									>
										fas fa-info-circle
									</k-icon>
								</template>
								<div class="k-ps-tooltip-content">
									The Identifier for the framework-level standard that every top-level standard is
									filed under. Normally built from the framework title and approved year; shown here
									because that value is already in use as a standard's code. Whatever you set is kept
									for future exports, since changing it later would re-file the whole framework.
								</div>
							</k-tooltip>
							<b>Framework Identifier:</b>
						</div>
						<k-text-field dense outlined hide-details="auto" v-bind="formFields.document_identifier" @input="formFields.document_identifier.input" @blur="formFields.document_identifier.blur" placeholder="e.g. MA19DOC" class="k-ps-inline-field k-ps-settings-field"></k-text-field>
					</div>
					<div v-if="approved_year_applies" class="d-flex align-center my-3">
						<div class="mr-2 k-ps-settings-label">
							<k-tooltip bottom>
								<template v-slot:activator="{ on }">
									<k-icon
										v-on="on"
										small
										color="light-blue"
										class="k-ps-tooltip-icon"
									>
										fas fa-info-circle
									</k-icon>
								</template>
								<div class="k-ps-tooltip-content">
									The two-digit year the framework was approved — not necessarily the year it is
									adopted or implemented.
								</div>
							</k-tooltip>
							<b>Approved Year:</b>
						</div>
						<k-text-field dense outlined hide-details="auto" v-bind="formFields.approved_year" @input="formFields.approved_year.input" @blur="formFields.approved_year.blur" placeholder="e.g. 25" maxlength="2" class="k-ps-inline-field k-ps-settings-field"></k-text-field>
					</div>
					<div class="d-flex align-center my-3">
						<div class="mr-2 k-ps-settings-label">
							<k-tooltip bottom>
								<template v-slot:activator="{ on }">
									<k-icon
										v-on="on"
										small
										color="light-blue"
										class="k-ps-tooltip-icon"
									>
										fas fa-info-circle
									</k-icon>
								</template>
								<div class="k-ps-tooltip-content">
									The school year in which the framework is being implemented — PowerSchool's
									internal identifier for that year.
								</div>
							</k-tooltip>
							<b>YearID:</b>
						</div>
						<k-select dense outlined hide-details="auto" :items="year_id_options" v-bind="formFields.year_id" @input="formFields.year_id.input" @blur="formFields.year_id.blur" :menu-props="{top:false,dense:true}" class="k-ps-inline-field k-ps-settings-field"></k-select>
					</div>
					<div class="k-ps-settings-hint">International schools should verify their YearID values before importing.</div>
					<div class="d-flex align-center my-3">
						<div class="mr-2 k-ps-settings-label">
							<k-tooltip bottom>
								<template v-slot:activator="{ on }">
									<k-icon
										v-on="on"
										small
										color="light-blue"
										class="k-ps-tooltip-icon"
									>
										fas fa-info-circle
									</k-icon>
								</template>
								<div class="k-ps-tooltip-content">
									The PowerSchool subject area for these standards (e.g. MATH). Defaults to the
									framework's subject.
								</div>
							</k-tooltip>
							<b>SubjectArea:</b>
						</div>
						<k-text-field dense outlined hide-details="auto" v-bind="formFields.subject_area" @input="formFields.subject_area.input" @blur="formFields.subject_area.blur" placeholder="e.g. MATH" class="k-ps-inline-field k-ps-settings-field"></k-text-field>
					</div>
					<div class="d-flex align-center my-3">
						<div class="mr-2 k-ps-settings-label">
							<k-tooltip bottom>
								<template v-slot:activator="{ on }">
									<k-icon
										v-on="on"
										small
										color="light-blue"
										class="k-ps-tooltip-icon"
									>
										fas fa-info-circle
									</k-icon>
								</template>
								<div class="k-ps-tooltip-content">
									The maximum number of characters allowed for a comment on each standard in PowerSchool.
								</div>
							</k-tooltip>
							<b>MaxCommentLength:</b>
						</div>
						<k-text-field dense outlined hide-details="auto" v-bind="formFields.max_comment_length" @input="formFields.max_comment_length.input" @blur="formFields.max_comment_length.blur" placeholder="e.g. 200" class="k-ps-inline-field k-ps-settings-field"></k-text-field>
					</div>
					<div class="d-flex align-center my-3">
						<div class="mr-2 k-ps-settings-label">
							<k-tooltip bottom>
								<template v-slot:activator="{ on }">
									<k-icon
										v-on="on"
										small
										color="light-blue"
										class="k-ps-tooltip-icon"
									>
										fas fa-info-circle
									</k-icon>
								</template>
								<div class="k-ps-tooltip-content">
									The DCID of the PowerSchool grade scale item to associate with these standards.
								</div>
							</k-tooltip>
							<b>GradeScaleItemDCID:</b>
						</div>
						<k-text-field dense outlined hide-details="auto" v-bind="formFields.grade_scale_item_dcid" @input="formFields.grade_scale_item_dcid.input" @blur="formFields.grade_scale_item_dcid.blur" placeholder="e.g. 1" class="k-ps-inline-field k-ps-settings-field"></k-text-field>
					</div>
					<div class="d-flex align-center my-3">
						<div class="mr-2 k-ps-settings-label">
							<k-tooltip bottom>
								<template v-slot:activator="{ on }">
									<k-icon
										v-on="on"
										small
										color="light-blue"
										class="k-ps-tooltip-icon"
									>
										fas fa-info-circle
									</k-icon>
								</template>
								<div class="k-ps-tooltip-content">
									Comma-separated list of course numbers these standards should be primarily
									attached to in PowerSchool.
								</div>
							</k-tooltip>
							<b>PrimaryCourses:</b>
						</div>
						<k-text-field dense outlined hide-details="auto" v-bind="formFields.primary_courses" @input="formFields.primary_courses.input" @blur="formFields.primary_courses.blur" placeholder="Comma-separated" class="k-ps-inline-field k-ps-settings-field"></k-text-field>
					</div>
				</div>
				<div class="d-flex justify-end mt-3">
					<k-btn
						small
						class="k-tight-btn mr-2"
						color="primary"
						@click="stage = 2"
					><k-icon
						small
						class="mr-2"
					>fas fa-arrow-left</k-icon
					>Back</k-btn
					>
					<k-btn
						small
						class="k-tight-btn"
						color="primary"
						@click="formHandleSubmit"
					>Go To Step 4
						<k-icon
							small
							class="ml-2"
						>fas fa-arrow-right</k-icon
						></k-btn
					>
				</div>
			</div>
			<div
				v-if="stage == 4"
				class="mt-4"
			>
				<div class="d-flex align-end">
					<div>
						<h3 class="pink--text text--darken-4">Ready to Download</h3>
						<div class="mt-3">
							<b>Step 4:</b> Click to download a CSV file with:
							<ul>
								<li>
									{{ row_objects.length }} total {{ U.ps('row', row_objects.length) }}
								</li>
							</ul>
						</div>
					</div>
					<k-spacer />
					<div class="ml-4 k-ps-action-column">
						<k-btn
							small
							class="k-tight-btn mb-2"
							color="primary"
							block
							@click="stage = 3"
						><k-icon
							small
							class="mr-2"
						>fas fa-arrow-left</k-icon
						>Back</k-btn
						>
						<k-btn
							class="k-tight-btn"
							color="pink darken-4"
							dark
							block
							@click="download_csv"
						>Download CSV
							<k-icon
								small
								class="ml-2"
							>fas fa-file-arrow-down</k-icon
							></k-btn
						>
					</div>
				</div>
			</div>
		</k-card-text>
	</k-card></k-dialog
	>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { useFormMixin } from '@cglt/ui-legacy'
import { z } from 'zod'

// PowerSchool YearID formula: (2-digit year) + 10, with base year 1990
const POWERSCHOOL_YEAR_ID_OFFSET = 10
const POWERSCHOOL_BASE_YEAR = 1990
const JUNE = 6

// field limits
const MAX_IDENTIFIER_LENGTH = 50
const MAX_PREFIX_LENGTH = 20
const MAX_NAME_LENGTH = 400
const MAX_DESCRIPTION_LENGTH = 4000
const MAX_SUBJECT_AREA_LENGTH = 40
const MAX_COMMENT_LENGTH = 4000
const DEFAULT_MAX_COMMENT_LENGTH = '200'
const DEFAULT_GRADE_SCALE_ITEM_DCID = '1'

// tree depth limit for Case 2 identifiers
const MAX_TREE_DEPTH = 30

const MAX_CLASHES_NAMED = 3

const DIALOG_MAX_WIDTH_BRANCH = 640
const DIALOG_MAX_WIDTH_DEFAULT = 860

const IGNORED_TITLE_WORDS = new Set(['and', 'or', 'the', 'of', 'a', 'an', 'for', 'to', 'in'])

const html_comment_regex = /<!--[\s\S]*?-->/g
const orphan_comment_opener_regex = /<!--/g
const html_declaration_regex = /<!(?:DOCTYPE|\[)[^>]*>/gi
const xml_processing_instruction_regex = /<\?[^>]*\?>/g
const html_tag_regex = /<\/?[a-zA-Z][^>]*>/g

// a tag needs a letter after '<' so that math comparisons survive; '<!' and '<?' can't be math.
// an unterminated opener is dropped on its own rather than to the next '>', which would take
// the prose with it
const strip_html = (s) => s
	.replace(html_comment_regex, '')
	.replace(orphan_comment_opener_regex, '')
	.replace(html_declaration_regex, '')
	.replace(xml_processing_instruction_regex, '')
	.replace(html_tag_regex, '')

// PowerSchool rejects carriage returns, line feeds, bullets and indenting in Name and
// Description; every text field bound for those columns goes through here
const sanitize_text = (s) => strip_html(s)
	.replace(/[\r\n\t•‣◦⁃∙]/g, ' ')
	.replace(/\s+/g, ' ')
	.trim()

// normalization to ensure that we can identify matching codes.
const normalize_hcs = (cfitem) => strip_html(cfitem.humanCodingScheme || '').substring(0, MAX_IDENTIFIER_LENGTH).trim()

const settings_schema = z.object({
	identifier_prefix: z.string().max(MAX_PREFIX_LENGTH, `Must be ${MAX_PREFIX_LENGTH} characters or less`),
	document_identifier: z.string().max(MAX_IDENTIFIER_LENGTH, `Must be ${MAX_IDENTIFIER_LENGTH} characters or less`),
	approved_year: z.string().regex(/^\d*$/, 'Must be numeric'),
	year_id: z.string(),
	subject_area: z.string().max(MAX_SUBJECT_AREA_LENGTH, `Must be ${MAX_SUBJECT_AREA_LENGTH} characters or less`),
	max_comment_length: z.string().regex(/^\d*$/, 'Must be numeric').refine(
		val => !val || Number(val) <= MAX_COMMENT_LENGTH,
		{ message: `Must be ${MAX_COMMENT_LENGTH} or less` },
	),
	grade_scale_item_dcid: z.string().regex(/^\d*$/, 'Must be numeric'),
	primary_courses: z.string(),
})

export default {
	mixins: [
		useFormMixin({
			schema: settings_schema,
			onSubmit: function (values, formAddError) {
				this.submit_stage_3(values, formAddError)
			},
		}),
	],
	props: {
		framework_record: { type: Object, required: true },
	},
	data() {
		return {
			dialog_open: true,
			stage: 1,
			rows: [],
			row_objects: [],
			item_types: {},
			branches_to_import: [],
			preserved_codes_lowercased: new Set(),
			items_keeping_code: new Set(),
			preserved_count: 0,
			generated_count: 0,
			prefix_clashes: [],
			clashing_document_identifier: '',
			document_identifier_field_shown: false,
			generated_document_clash: '',
			all_hcs_unique: false,
			needs_prefix: false,
		}
	},
	computed: {
		...mapState(['site_config']),
		...mapGetters([]),
		dialog_max_width() {
			return this.stage == 1 ? DIALOG_MAX_WIDTH_BRANCH : DIALOG_MAX_WIDTH_DEFAULT
		},
		show_document_identifier_field() {
			return this.document_identifier_field_shown || !!this.clashing_document_identifier || !empty(this.form.values.document_identifier)
		},
		document_identifier_override() {
			return (this.form.values.document_identifier || '').trim()
		},
		approved_year_applies() {
			return this.all_hcs_unique && this.exporting_all && !this.document_identifier_override
		},
		prefix_clash_message() {
			const clash_count = this.prefix_clashes.length
			const named_clashes = this.prefix_clashes.slice(0, MAX_CLASHES_NAMED).join(', ')
			const unnamed_count = clash_count - MAX_CLASHES_NAMED
			const overflow_text = unnamed_count > 0 ? ` and ${unnamed_count} more` : ''
			return `Would produce ${U.ps('an identifier', clash_count, 'identifiers')} matching ${U.ps('a code', clash_count, 'codes')} already in this framework (${named_clashes}${overflow_text}). Choose a different prefix.`
		},
		generated_document_clash_message() {
			return `"${this.generated_document_clash}" is also the identifier that would be generated for a standard elsewhere in this framework. Choose a different identifier for the framework-level standard.`
		},
		document_identifier_clash_message() {
			return `"${this.clashing_document_identifier}" is already the code for a standard in this framework. Choose a different identifier for the framework-level standard.`
		},
		identifier_strategy_hint() {
			const kept = this.preserved_count
			const generated = this.generated_count
			return `Across this framework, ${kept} ${U.ps('standard', kept, 'standards')} will keep ${kept == 1 ? 'its' : 'their'} existing code and ${generated} will get ${U.ps('a generated one', generated, 'generated ones')}.`
		},
		selection_label() {
			const n = this.branches_to_import.length
			return n == 0 ? 'Entire framework' : `${n} ${U.ps('branch', n, 'branches')} selected`
		},
		exporting_all() {
			if (this.branches_to_import.length == 0) return true
			const all_branches = this.framework_record.cfo.cftree.children
			const selected_branch_set = new Set(this.branches_to_import)
			return all_branches.every(node => selected_branch_set.has(node.cfitem.identifier))
		},
		year_id_options() {
			const now = new Date()
			const month = now.getMonth() + 1
			const year = now.getFullYear()
			let current_year_id, next_year_id

			if (month <= JUNE) {
				current_year_id = (year - 1) % 100 + POWERSCHOOL_YEAR_ID_OFFSET
				next_year_id = year % 100 + POWERSCHOOL_YEAR_ID_OFFSET
			} else {
				current_year_id = year % 100 + POWERSCHOOL_YEAR_ID_OFFSET
				next_year_id = (year + 1) % 100 + POWERSCHOOL_YEAR_ID_OFFSET
			}

			const school_year_label = (id) => `${POWERSCHOOL_BASE_YEAR + id}-${POWERSCHOOL_BASE_YEAR + 1 + id} SY`
			return [
				{ value: String(current_year_id), text: `Current School Year (${school_year_label(current_year_id)})` },
				{ value: String(next_year_id), text: `Next School Year (${school_year_label(next_year_id)})` },
			]
		},
	},
	watch: {
		item_types: {
			immediate: false,
			deep: true,
			handler(val) {
				this.$store.commit('lst_set_hash', [
					'powerschool_export_item_types',
					this.framework_record.lsdoc_identifier,
					this.item_types,
				])
			},
		},
		approved_year_applies(newVal) {
			if (newVal) return
			const shape = settings_schema.shape
			try { shape.approved_year.parse(this.form.values.approved_year) }
			catch { this.$set(this.form.values, 'approved_year', '') }
		},
	},
	created() {
		vapp.powerschool_import_component = this

		// pull initial branches_to_import from lst, filtering out any that no longer exist in the framework
		this.branches_to_import = (
			this.$store.state.lst.powerschool_branches_to_import[this.framework_record.lsdoc_identifier] ?? []
		).filter(identifier => !!this.framework_record.cfo.cfitems[identifier])

		// get initial settings from lst
		const saved_settings = object_copy(this.$store.state.lst.powerschool_export_settings?.[this.framework_record.lsdoc_identifier] ?? {})
		const document_subjects = U.entity_subject_string_array(this.framework_record.json.CFDocument)

		// resolved defaults for each form field; used whenever the LST value is empty
		const defaults = {
			identifier_prefix: '',
			document_identifier: '',
			approved_year: '',
			year_id: this.year_id_options[0].value,
			subject_area: document_subjects[0] || '',
			max_comment_length: DEFAULT_MAX_COMMENT_LENGTH,
			grade_scale_item_dcid: DEFAULT_GRADE_SCALE_ITEM_DCID,
			primary_courses: '',
		}

		Object.keys(settings_schema.shape).forEach(key => {
			const value = empty(saved_settings[key]) ? defaults[key] : saved_settings[key]
			this.$set(this.form.values, key, value)
		})

		this.document_identifier_field_shown = !empty(this.form.values.document_identifier)

		// start on stage 1; have to delay this a scoche so that the hide_fn (see below) doesn't immediately return true and close SatchelInline
		setTimeout((x) => this.go_to_stage_1())
	},
	mounted() {},
	methods: {
		show_branch_chooser() {
			// push the dialog over to the left, so we have room for the chooser on the right
			setTimeout((x) => $('.k-powerschool-import-dialog').parent().css('justify-content', 'flex-start'), 100)

			const data = {
				framework_identifier: this.framework_record.lsdoc_identifier,
				selected_items: object_copy(this.branches_to_import),
			}

			const show_data = {
				hide_fn: () => {
					return $('.k-powerschool-import-card').is(':visible') == false
				},
				show_left_controls: false,
				force_size: 'small',
			}

			vapp.$refs.satchel.execute('show', show_data).then(() => {
				vapp.$refs.satchel.execute('load_framework', data).then(() => {
					vapp.$refs.satchel.execute('chooser', { chooser_mode: true }).then((chosen_item) => {
						let i = this.branches_to_import.findIndex(
							(identifier) => identifier == chosen_item.cfitem.identifier
						)
						if (i > -1) {
							this.branches_to_import.splice(i, 1)
						} else {
							this.branches_to_import.push(chosen_item.cfitem.identifier)
						}
						this.$store.commit('lst_set_hash', [
							'powerschool_branches_to_import',
							this.framework_record.lsdoc_identifier,
							object_copy(this.branches_to_import),
						])
						this.show_branch_chooser()
					})
				})
			})
		},

		hide_branch_chooser() {
			vapp.$refs.satchel.execute('hide')
			$('.k-powerschool-import-dialog').parent().css('justify-content', 'center')
		},

		go_to_stage_1() {
			this.show_branch_chooser()
			this.stage = 1
		},

		go_to_stage_2() {
			this.hide_branch_chooser()

			// recursive fn for finding types
			const found_types = []
			const counts = {}
			const has_outcome = {}

			const find_item_types = (node) => {
				const is_document = (node === this.framework_record.cfo.cftree)
				if (!is_document) {
					const type = U.item_type_string(node.cfitem) || '(No Type)'
					if (!found_types.includes(type)) {
						found_types.push(type)
						counts[type] = 0
						has_outcome[type] = false
					}
					++counts[type]
					if (node.cfitem.is_outcome) has_outcome[type] = true
				}

				for (const child of node.children) {
					find_item_types(child)
				}
			}

			if (this.branches_to_import.length == 0) {
				find_item_types(this.framework_record.cfo.cftree)
			} else {
				for (const branch_identifier of this.branches_to_import) {
					find_item_types(this.framework_record.cfo.cfitems[branch_identifier].tree_nodes[0])
				}
			}

			// make the order of sorted_types match the order in cfo's item_types
			const sorted_types = []
			for (const type of this.framework_record.cfo.item_types) {
				if (found_types.includes(type)) sorted_types.push(type)
			}
			// append (No Type) at the end if present
			if (found_types.includes('(No Type)') && !sorted_types.includes('(No Type)')) sorted_types.push('(No Type)')

			// get previously-stored item type settings for this framework
			const saved_item_types =
				this.$store.state.lst.powerschool_export_item_types[this.framework_record.lsdoc_identifier] || {}

			// construct updated item type settings with per-type booleans
			const item_type_settings = {}
			for (let i = 0; i < sorted_types.length; ++i) {
				const item_type = sorted_types[i]
				const saved = saved_item_types[item_type]

				item_type_settings[item_type] = {
					include: true,
					IsAssignmentAllowed: saved?.IsAssignmentAllowed ?? has_outcome[item_type],
					IsActive: saved?.IsActive ?? true,
					IsCommentIncluded: saved?.IsCommentIncluded ?? false,
					IsExcludedFromReports: saved?.IsExcludedFromReports ?? false,
					count: counts[item_type],
				}
			}
			this.item_types = item_type_settings

			this.stage = 2
		},

		detect_hcs_usability() {
			const preserved_codes_lowercased = new Set()
			const item_identifiers_keeping_code = new Set()
			let non_alias_item_count = 0

			// scan the full tree so identifiers are consistent across separate branch uploads
			const scan = (node) => {
				const is_document = (node === this.framework_record.cfo.cftree)
				if (!is_document) {
					const cfitem = node.cfitem
					const is_alias = cfitem.tree_nodes && cfitem.tree_nodes[0] !== node

					if (!is_alias) {
						++non_alias_item_count
						const hcs = normalize_hcs(cfitem)

						// the first item to use a code keeps it and any later item sharing that code
						// generates, so a duplicate can never take the code off the original
						if (hcs && !preserved_codes_lowercased.has(hcs.toLowerCase())) {
							preserved_codes_lowercased.add(hcs.toLowerCase())
							item_identifiers_keeping_code.add(cfitem.identifier)
						}
					}
				}

				for (const child of node.children) {
					scan(child)
				}
			}

			scan(this.framework_record.cfo.cftree)

			this.preserved_codes_lowercased = preserved_codes_lowercased
			this.items_keeping_code = item_identifiers_keeping_code
			this.preserved_count = item_identifiers_keeping_code.size
			this.generated_count = non_alias_item_count - item_identifiers_keeping_code.size

			this.all_hcs_unique = (this.generated_count == 0)
			this.needs_prefix = this.generated_count > 0
		},

		go_to_stage_3() {
			// persist item type settings
			this.$store.commit('lst_set_hash', [
				'powerschool_export_item_types',
				this.framework_record.lsdoc_identifier,
				this.item_types,
			])

			this.detect_hcs_usability()

			this.stage = 3
		},

		submit_stage_3(values, formAddError) {
			let has_error = false
			if (this.needs_prefix && !(values.identifier_prefix || '').trim()) {
				formAddError('identifier_prefix', 'Required')
				has_error = true
			}
			if (this.approved_year_applies) {
				if (!/^\d{2}$/.test(values.approved_year || '')) {
					formAddError('approved_year', 'Must be 2 digits')
					has_error = true
				}
			}
			if (has_error) return

			this.$set(this.form.values, 'max_comment_length', values.max_comment_length || DEFAULT_MAX_COMMENT_LENGTH)

			this.build_row_objects()

			if (this.clashing_document_identifier) {
				// prefill so the user edits from the value they were expecting rather than a blank
				if (empty(this.form.values.document_identifier)) {
					this.$set(this.form.values, 'document_identifier', this.clashing_document_identifier)
				}
				formAddError('document_identifier', this.document_identifier_clash_message)
				return
			}

			if (this.prefix_clashes.length) {
				formAddError('identifier_prefix', this.prefix_clash_message)
				return
			}

			if (this.generated_document_clash) {
				formAddError('document_identifier', this.generated_document_clash_message)
				return
			}

			this.$store.commit('lst_set_hash', ['powerschool_export_settings', this.framework_record.lsdoc_identifier, object_copy(this.form.values)])

			U.loading_start()
			setTimeout(() => {
				this.build_csv_rows()
				U.loading_stop()
				this.stage = 4
			}, 500)
		},

		build_row_objects() {
			// PowerSchool standard import CSV columns (15):
			// ParentStandardIdentifier, YearID, Name, Description, Identifier,
			// SubjectArea, GradeScaleItemDCID, IsAssignmentAllowed, IsActive, DisplayPosition,
			// IsCommentIncluded, MaxCommentLength, IsExcludedFromReports, PrimaryCourses, CASEId

			const row_objects = []
			const prefix = (this.form.values.identifier_prefix || '').trim()

			const primary_courses = this.form.values.primary_courses
				.split(',')
				.map(s => s.trim())
				.filter(s => s)
				.join(',')

			// tree_sequence tracking for Case 2 identifiers (ported from ItemsTable.vue)
			const tree_sequence = new Array(MAX_TREE_DEPTH).fill(0)

			// per-parent child counter for DisplayPosition (sibling ordinal)
			const parent_child_counters = new Map()

			// build a set of selected branch identifiers for quick lookup
			const selected_branch_set = new Set(this.branches_to_import)

			// add a document-level parent row so all top-level items are grouped under it,
			// but only when exporting the entire framework (no branches selected, or all branches selected)
			const doc = this.framework_record.json.CFDocument
			const doc_title = sanitize_text(doc.title || '')

			const document_identifier_override = this.document_identifier_override

			let doc_identifier

			if (document_identifier_override) {
				doc_identifier = document_identifier_override.substring(0, MAX_IDENTIFIER_LENGTH)
			} else if (this.all_hcs_unique) {
				// Case 1: first letters of title words + year
				const first_letters = doc_title
					.split(/\s+/)
					.filter(w => w && !IGNORED_TITLE_WORDS.has(w.toLowerCase()))
					.map(w => (w[0] || '').replace(/[^A-Za-z0-9]/g, ''))
					.join('') || 'FRAMEWORK'

				doc_identifier = (first_letters + (this.form.values.approved_year || '')).substring(0, MAX_IDENTIFIER_LENGTH)
			} else {
				// Case 2: use the prefix itself
				doc_identifier = (prefix || '').substring(0, MAX_IDENTIFIER_LENGTH)
			}

			const exporting_all = this.exporting_all

			if (exporting_all) {
				row_objects.push({
					ParentStandardIdentifier: '',
					YearID: this.form.values.year_id,
					Name: U.truncate_to_word(doc_title, MAX_NAME_LENGTH).trim(),
					Description: U.truncate_to_word(doc_title, MAX_DESCRIPTION_LENGTH).trim(),
					Identifier: doc_identifier,
					SubjectArea: this.form.values.subject_area,
					GradeScaleItemDCID: this.form.values.grade_scale_item_dcid,
					IsAssignmentAllowed: true,
					IsActive: true,
					DisplayPosition: 1,
					IsCommentIncluded: false,
					MaxCommentLength: this.form.values.max_comment_length,
					IsExcludedFromReports: false,
					PrimaryCourses: primary_courses,
					CASEId: doc.identifier,
				})
			}

			const prefix_clashes = []
			let generated_document_clash = ''
			this.clashing_document_identifier = ''

			if (exporting_all && this.preserved_codes_lowercased.has(doc_identifier.toLowerCase())) {
				const document_identifier_is_the_prefix = !document_identifier_override && this.needs_prefix
				if (document_identifier_is_the_prefix) prefix_clashes.push(doc_identifier)
				else {
					this.clashing_document_identifier = doc_identifier
					this.document_identifier_field_shown = true
				}
			}

			const claimed_identifiers = new Set(this.preserved_codes_lowercased)
			claimed_identifiers.add(doc_identifier.toLowerCase())
			const claim_unique_identifier = (identifier) => {
				let candidate_identifier = identifier
				for (let n = 2; claimed_identifiers.has(candidate_identifier.toLowerCase()); ++n) {
					const suffix = '_' + n
					candidate_identifier = identifier.substring(0, MAX_IDENTIFIER_LENGTH - suffix.length) + suffix
				}
				claimed_identifiers.add(candidate_identifier.toLowerCase())
				return candidate_identifier
			}

			/////////////////////////////
			// recursive function for adding rows
			// in_selected_branch: whether we are inside a selected branch (always true when no branches are selected)
			const add_row = (node, parent_identifier, level, in_selected_branch) => {
				const cfitem = node.cfitem
				const is_document = (node === this.framework_record.cfo.cftree)
				const item_type_str = U.item_type_string(cfitem) || '(No Type)'
				const item_type_config = this.item_types[item_type_str]
				const is_alias = cfitem.tree_nodes && cfitem.tree_nodes[0] !== node
				const should_include = !is_document && item_type_config?.include && !is_alias

				// check if this node is a selected branch root
				if (!in_selected_branch && selected_branch_set.has(cfitem.identifier)) {
					in_selected_branch = true
				}

				let current_identifier = parent_identifier

				// track tree_sequence and identifier for ALL non-document items,
				// regardless of should_include, so values reflect the full tree
				if (!is_document) {
					// tree_sequence_value for Case 2 identifiers
					++tree_sequence[level]
					let tree_sequence_value = ''

					for (let i = 0; i < MAX_TREE_DEPTH; ++i) {
						if (i <= level) {
							if (tree_sequence_value) tree_sequence_value += '.'
							tree_sequence_value += tree_sequence[i]
						} else {
							tree_sequence[i] = 0
						}
					}

					const hcs = normalize_hcs(cfitem)

					let identifier

					if (this.items_keeping_code.has(cfitem.identifier)) {
						identifier = hcs
					} else {
						const generated_base = (prefix ? prefix + '.' + tree_sequence_value : tree_sequence_value).substring(0, MAX_IDENTIFIER_LENGTH)

						// an alias emits no row in any export, so its base can't clash with anything.
						// unselected branches and unchecked types are still checked, so one prefix
						// stays valid however the user configures the next export
						if (prefix && !is_alias) {
							if (this.preserved_codes_lowercased.has(generated_base.toLowerCase())) prefix_clashes.push(generated_base)
							else if (generated_base.toLowerCase() === doc_identifier.toLowerCase()) generated_document_clash = generated_base
						}

						identifier = claim_unique_identifier(generated_base)
					}
					current_identifier = identifier

					// DisplayPosition: sibling ordinal within parent, based on full tree
					const ordinal = (parent_child_counters.get(parent_identifier) || 0) + 1
					parent_child_counters.set(parent_identifier, ordinal)

					if (should_include && in_selected_branch) {
						const full_statement = cfitem.fullStatement || ''
						const abbreviated_statement = cfitem.abbreviatedStatement || ''

						const base_name = abbreviated_statement ? abbreviated_statement : full_statement
						let name = base_name
						let description = full_statement

						// trim last: truncating can land the cut just after a space, and PowerSchool
						// rejects trailing spaces
						name = U.truncate_to_word(sanitize_text(name), MAX_NAME_LENGTH).trim()
						description = U.truncate_to_word(sanitize_text(description), MAX_DESCRIPTION_LENGTH).trim()

						row_objects.push({
							ParentStandardIdentifier: parent_identifier,
							YearID: this.form.values.year_id,
							Name: name,
							Description: description,
							Identifier: identifier,
							SubjectArea: this.form.values.subject_area,
							GradeScaleItemDCID: this.form.values.grade_scale_item_dcid,
							IsAssignmentAllowed: !!item_type_config.IsAssignmentAllowed,
							IsActive: !!item_type_config.IsActive,
							DisplayPosition: ordinal,
							IsCommentIncluded: !!item_type_config.IsCommentIncluded,
							MaxCommentLength: this.form.values.max_comment_length,
							IsExcludedFromReports: !!item_type_config.IsExcludedFromReports,
							PrimaryCourses: primary_courses,
							CASEId: cfitem.identifier,
						})
					}
				}

				// level increments for any non-document node; document root is a pass-through
				const child_level = !is_document ? level + 1 : level

				for (const child_node of node.children) {
					add_row(child_node, current_identifier, child_level, in_selected_branch)
				}
			}

			// always walk the full tree so tree_sequence values reflect the whole framework
			const no_branches_selected = this.branches_to_import.length == 0
			const root_parent_identifier = exporting_all ? doc_identifier : ''

			add_row(this.framework_record.cfo.cftree, root_parent_identifier, 0, no_branches_selected)

			this.row_objects = row_objects
			this.prefix_clashes = prefix_clashes
			this.generated_document_clash = generated_document_clash
		},

		build_csv_rows() {
			const rows = []
			const header = [
				'ParentStandardIdentifier',
				'YearID',
				'Name',
				'Description',
				'Identifier',
				'SubjectArea',
				'GradeScaleItemDCID',
				'IsAssignmentAllowed',
				'IsActive',
				'DisplayPosition',
				'IsCommentIncluded',
				'MaxCommentLength',
				'IsExcludedFromReports',
				'PrimaryCourses',
				'CASEId',
			]
			rows.push(header)

			for (const obj of this.row_objects) {
				rows.push([
					obj.ParentStandardIdentifier,
					obj.YearID,
					obj.Name,
					obj.Description,
					obj.Identifier,
					obj.SubjectArea,
					obj.GradeScaleItemDCID,
					obj.IsAssignmentAllowed ? 1 : 0,
					obj.IsActive ? 1 : 0,
					obj.DisplayPosition,
					obj.IsCommentIncluded ? 1 : 0,
					obj.MaxCommentLength,
					obj.IsExcludedFromReports ? 1 : 0,
					obj.PrimaryCourses,
					obj.CASEId,
				])
			}

			this.rows = rows
		},

		download_csv() {
			// record framework usage here, just before downloading file
			vapp.record_framework_download(this.framework_record.lsdoc_identifier)

			const date_string = date.format(new Date(), 'YYYY-MM-DD')
			const filename = `${this.framework_record.json.CFDocument.title}-POWERSCHOOL-${date_string}.csv`

			U.download_file(CSV.stringify(this.rows), filename)
		},
	},
}
</script>

<style lang="scss">
.k-powerschool-import-dialog {
	.k-ps-type-settings-table {
		width: 100%;
		border-collapse: collapse;
		--k-sticky-header-bg: #f5f5f5;
		th, td {
			padding: 4px 8px;
			font-size: 13px;
			border-bottom: 1px solid #eee;
		}
		th {
			text-align: left;
			font-weight: bold;
			background-color: #f5f5f5;
		}
	}
	.k-ps-dialog-title {
		border-bottom: 1px solid #999;
	}
	.k-powerschool-import-card {
		font-size: 16px;
		line-height: 21px;
	}
	.k-ps-info-callout {
		background-color: #eee;
		border-radius: 6px;
	}
	.k-ps-action-column {
		flex: 0 0 160px;
	}
	.k-ps-inline-field {
		min-height: 28px;
		.v-input__control {
			min-height: 28px !important;
		}
		input, .v-select__selection {
			font-size: 12px;
			padding: 2px 6px;
		}
	}
	$ps-label-width: 180px;
	$ps-label-gap: 8px; // matches Vuetify's .mr-2
	.k-ps-settings-label {
		min-width: $ps-label-width;
		font-size: 13px;
	}
	.k-ps-settings-field {
		max-width: 340px;
	}
	.k-ps-settings-hint {
		margin-left: $ps-label-width + $ps-label-gap;
		margin-top: -8px;
		font-size: 12px;
		color: #888;
	}
	.k-ps-tooltip-icon {
		margin-right: 4px;
		margin-top: -3px;
	}
}
.k-ps-tooltip-content {
	font-size: 14px;
	line-height: 18px;
	max-width: 400px;
}
</style>
